import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import {
  clearPendingGooglePhotosSave,
  friendlyGooglePhotosSaveError,
  markPendingGooglePhotosSave,
  peekPendingGooglePhotosSave,
  saveRestorationToGooglePhotos,
} from '../services/googlePhotosExport';

export type GooglePhotosExportPhase =
  | 'idle'
  | 'checking'
  | 'saving'
  | 'saved'
  | 'needs-connect'
  | 'error';

export function useGooglePhotosExport(options: {
  imageUrl?: string | null;
  fileName?: string;
  watermarkExportsEnabled?: boolean;
}) {
  const { imageUrl, fileName, watermarkExportsEnabled = true } = options;
  const { token, refreshBalance } = useAuth();
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [phase, setPhase] = useState<GooglePhotosExportPhase>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSavedTimer = () => {
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
  };

  const refreshStatus = useCallback(async () => {
    try {
      setPhase((p) => (p === 'saving' || p === 'saved' ? p : 'checking'));
      const status = await api.googlePhotosStatus(token);
      setConnected(Boolean(status.connected));
      setConfigured(status.configured !== false);
      setPhase((p) => (p === 'saving' || p === 'saved' ? p : 'idle'));
      return Boolean(status.connected);
    } catch {
      setConnected(false);
      setPhase((p) => (p === 'saving' || p === 'saved' ? p : 'idle'));
      return false;
    }
  }, [token]);

  useEffect(() => {
    refreshStatus();
    const onFocus = () => {
      refreshStatus();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearSavedTimer();
    };
  }, [refreshStatus]);

  // After OAuth return: if user came from Save intent, show a gentle nudge
  useEffect(() => {
    const intent = peekPendingGooglePhotosSave();
    if (!intent) return;
    if (!connected) return;
    clearPendingGooglePhotosSave();
    setMessage('Google Photos connected. Click Save to Google Photos to finish.');
  }, [connected]);

  const connect = useCallback(async () => {
    markPendingGooglePhotosSave(fileName);
    try {
      const { url } = await api.googlePhotosAuthUrl(token);
      window.location.href = url;
    } catch (err) {
      const friendly = friendlyGooglePhotosSaveError(err);
      setPhase('error');
      setMessage(friendly.message);
    }
  }, [fileName, token]);

  const save = useCallback(async () => {
    if (!imageUrl) {
      setPhase('error');
      setMessage('No restored image is selected to save.');
      return;
    }

    clearSavedTimer();
    setMessage(null);

    const isConnected = connected || (await refreshStatus());
    if (!isConnected) {
      setPhase('needs-connect');
      setMessage('Connect Google Photos to save this restoration directly to your library.');
      return;
    }

    setPhase('saving');
    try {
      await saveRestorationToGooglePhotos({
        imageUrl,
        fileName,
        watermarkExportsEnabled,
        token,
      });
      clearPendingGooglePhotosSave();
      setPhase('saved');
      setMessage('✓ Saved to Google Photos');
      try {
        await refreshBalance();
      } catch {
        /* non-fatal */
      }
      savedTimerRef.current = setTimeout(() => {
        setPhase('idle');
        setMessage(null);
      }, 4000);
    } catch (err) {
      const friendly = friendlyGooglePhotosSaveError(err);
      if (friendly.kind === 'connect') {
        setConnected(false);
        setPhase('needs-connect');
      } else if (friendly.kind === 'expired') {
        setConnected(false);
        setPhase('needs-connect');
      } else {
        setPhase('error');
      }
      setMessage(friendly.message);
    }
  }, [
    imageUrl,
    fileName,
    watermarkExportsEnabled,
    token,
    connected,
    refreshStatus,
    refreshBalance,
  ]);

  const dismissMessage = useCallback(() => {
    if (phase === 'saved') return;
    setMessage(null);
    if (phase === 'error' || phase === 'needs-connect') setPhase('idle');
  }, [phase]);

  return {
    connected,
    configured,
    phase,
    message,
    busy: phase === 'saving' || phase === 'checking',
    save,
    connect,
    refreshStatus,
    dismissMessage,
  };
}
